import { AutoUser } from './auto-user';
import { BinaryTree } from './binary-tree';
import { CoordinatorService } from './coordinator.service';
import { ProjectMember } from './project-member';
import { SEARCH_HELP, SEARCH_ITEM_HELP_DATA } from './search-archive$';
import { User } from './user';
import { XItem } from './x-item';

let WIDGET_COUNTER = 1;
let BT_COUNTER = 1;

let BT_RUN_COUNTER = 1;
class Sel {
    type: string;
    values: { [tag: string]: any };
    idSet: Set<string>;

    constructor(
        private coords: CoordinatorService,
        { type, values, idSet }: { type: string; values: any; idSet: Set<string> },
    ) {
        this.type = type;
        this.values = values;
        this.idSet = idSet;
    }

    get isActive() {
        return !!this.idSet.size;
    }

    private get _saveLists() {
        const andLs: string[] = [];
        const orLs: string[] = [];
        const notLs: string[] = [];

        Object.keys(this.values).forEach((k) => {
            switch (this.values[k]) {
                case 'and':
                    andLs.push(`"${k}"`);
                    break;
                case 'or':
                    orLs.push(`"${k}"`);
                    break;
                case 'not':
                    notLs.push(`"${k}"`);
                    break;
            }
        });
        return { andLs, orLs, notLs };
    }
    saveRun(ls: string[]) {
        if (!this.isActive) return;
        const { andLs, orLs, notLs } = this._saveLists;

        const sVar = `sel${BT_RUN_COUNTER++}`;
        const btConst = `tree${BT_RUN_COUNTER++}`;

        if (this.type === 'subset') {
            if (andLs.length) ls.push(`let ${sVar} = dt.and(subset([${andLs.join(', ')}]))`);
            else if (orLs.length) {
                ls.push(`let ${sVar} = subset(${orLs[0]})`);
                orLs.slice(1).forEach((s) => ls.push(`${sVar} = ${sVar}.or(subset(${s}))`));
            } else ls.push(`let ${sVar} = dt.hitset()`);
        } else {
            const bt = this.coords.binaryTree$.getTree(this.type);
            if (!bt) return;
            ls.push(`const ${btConst} = ${bt.type}("${this.type}")`);
            if (andLs.length)
                ls.push(`let ${sVar} = dt.and(${btConst}.node([${andLs.join(', ')}]))`);
            else if (orLs.length) {
                ls.push(`let ${sVar} = ${btConst}.node(${orLs[0]}).hitset()`);
                orLs.slice(1).forEach((s) =>
                    ls.push(`${sVar} = ${sVar}.or(${btConst}.node(${s}).hitset())`),
                );
            } else ls.push(`let ${sVar} = dt.hitset()`);
        }

        if (notLs.length) {
            if (this.type === 'subset')
                ls.push(`${sVar} = ${sVar}.not(subset([${notLs.join(', ')}]))`);
            else ls.push(`${sVar} = ${sVar}.not(${btConst}.node([${notLs.join(', ')}]))`);
        }
        // ls.push(`console.log('ZZZ', ${sVar})`);
        ls.push(`hitsetLs.push(${sVar})`);
    }
    saveHelp(ls: SEARCH_ITEM_HELP_DATA[]) {
        if (!this.isActive) return;
        const { andLs, orLs, notLs } = this._saveLists;
    }

    save(ls: string[]) {
        if (!this.isActive) return;
        const { andLs, orLs, notLs } = this._saveLists;

        const wConst = `widget${WIDGET_COUNTER++}`;

        if (this.type === 'subset') {
            ls.push(`const ${wConst} = selectionBoard.get("subset")`);
            andLs.length && ls.push(`${wConst}.and(subset([${andLs.join(', ')}]))`);
            orLs.length && ls.push(`${wConst}.or(subset([${orLs.join(', ')}]))`);
            notLs.length && ls.push(`${wConst}.not(subset([${notLs.join(', ')}]))`);
        } else {
            const bt = this.coords.binaryTree$.getTree(this.type);
            if (!bt) return;
            const btConst = `tree${BT_COUNTER++}`;
            ls.push(`const ${wConst} = selectionBoard.get("${this.type}")`);
            ls.push(`const ${btConst} = ${bt.type}("${this.type}")`);
            andLs.length && ls.push(`${wConst}.and(${btConst}.node([${andLs.join(', ')}]))`);
            orLs.length && ls.push(`${wConst}.or(${btConst}.node([${orLs.join(', ')}]))`);
            notLs.length && ls.push(`${wConst}.not(${btConst}.node([${notLs.join(', ')}]))`);
        }
        ls.push(`${wConst}.apply()`);
    }
}

export class Selection$ {
    private _selectionMap: Map<string, Sel> = new Map();
    private _idSet?: Set<string>;

    isCurrentOffSet = new Set<string>();
    isOtherOffSet = new Set<string>();

    constructor(private coords: CoordinatorService) {
        this.clear();
    }

    get isActive() {
        return !!this._selectionMap.size;
    }

    values(code: string) {
        return this._selectionMap.get(code)?.values;
    }

    hasActivePart(code: string) {
        return this._selectionMap.has(code);
    }

    process(ls: XItem[]) {
        if (!this.isActive) return ls;
        return ls.filter((xitm) => this._idSet?.has(xitm.id));
    }

    runInstance(code: string, script: string, params: any) {
        if (Object.keys(params).length > 0) {
            const resp = this.coords.script$.run(script);
            const s = new Sel(this.coords, { type: code, idSet: resp, values: params });
            this._selectionMap.set(code, s);
        } else {
            this._selectionMap.delete(code);
        }

        if (this._selectionMap.size) this._run();
        else this._idSet = undefined;

        this.coords.resultList$.markForCheck();
    }

    private _run() {
        let isFirst = true;
        let s: Set<string>;

        this._selectionMap.forEach((sel) => {
            if (isFirst) {
                s = new Set(sel.idSet);
                isFirst = false;
            } else
                Array.from(s).forEach((id) => {
                    if (!sel.idSet.has(id)) s.delete(id);
                });
        });

        this._idSet = s!;
    }

    clear() {
        this._selectionMap.clear();
    }

    clearButtonAction() {
        this.clear();
        this._run();
        this.coords.resultList$.markForCheck();
    }

    searchBT(
        bt: BinaryTree,
        testLs: {
            operator: 'and' | 'or' | 'not';
            id: string;
        }[],
        uLs: (User | AutoUser | ProjectMember)[],
    ) {
        if (!testLs.length || !uLs.length) {
            this._selectionMap.delete(bt.id);
            if (this._selectionMap.size) this._run();
            else this._idSet = undefined;
            this.coords.resultList$.markForCheck();
            return;
        }

        testLs.sort((a, b) => {
            if (a.operator === 'not') return -1;
            if (b.operator === 'not') return 1;
            return -1;
        });

        const selectedMp = new Map<string, Set<string>>();

        uLs.forEach((u) => {
            const itmId2btMap = u.btMap;
            itmId2btMap.forEach((btMap, itmId) => {
                const selectedLs = btMap.get(bt.id);
                if (!selectedLs?.length) return;
                let s = selectedMp.get(itmId);
                if (!s) {
                    s = new Set();
                    selectedMp.set(itmId, s);
                }
                selectedLs.forEach((ndId) => s.add(ndId));
            });
        });

        const activeSet = new Set<string>();
        const isNotOnly = testLs[testLs.length - 1].operator === 'not';

        this.coords.dataset$.main.mainIdList.forEach((xitmId) => {
            const selLs = selectedMp.get(xitmId);
            if (!selLs) {
                isNotOnly && activeSet.add(xitmId);
                return;
            }
            for (const { operator, id } of testLs) {
                if (operator === 'not' && selLs.has(id)) return;
                else if (operator === 'and' && !selLs.has(id)) return;
                else if (operator === 'or' && selLs.has(id)) {
                    activeSet.add(xitmId);
                    return;
                }
            }
            activeSet.add(xitmId);
        });

        this._selectionMap.set(
            bt.id,
            new Sel(this.coords, { type: bt.id, idSet: activeSet, values: { bt, testLs, uLs } }),
        );
        this._run();

        this.coords.resultList$.markForCheck();
    }

    search(
        type: string,
        testLs: {
            operator: 'and' | 'or' | 'not';
            id: string;
        }[],
        userIdLs?: string[],
    ) {
        const ls: string[] = [];
        const params: any = {};

        let fun = type;

        let isFirst = true;
        let finalPrefix = '';

        const bt = this.coords.binaryTree$.getTree(fun);

        let btUserArg: string | undefined;

        if (bt) {
            finalPrefix = `const bt = ${bt.type}("${bt.id}");`;
            fun = `bt.node`;
            btUserArg = userIdLs?.map((x) => `"${x}"`).join(', ');
            if (btUserArg) btUserArg = `.hitset(false, [${btUserArg}])`;
        }

        if (type == 'archived-search') fun = `$searchArchive.get`;

        testLs.forEach((t) => {
            let s = `${fun}("${t.id}")`;
            if (btUserArg) s += btUserArg;
            params[t.id] = t.operator;
            if (t.operator === 'or' && isFirst) {
                isFirst = false;
                ls.push(`and(${s})`);
            } else {
                ls.push(`${t.operator}(${s})`);
            }
        });

        const script = `${finalPrefix}return $collection.${ls.join('.')}.idSet`;

        this.coords.selection$.runInstance(type, script, params);
    }

    save(ls: string[]) {
        if (!this.isActive) return;
        ls.push(`const selectionBoard = searchInterface.widget('selection-board')`);
        Array.from(this._selectionMap.values()).forEach((sel) => sel.save(ls));
    }

    saveRun(ls: string[]) {
        if (!this.isActive) return;
        Array.from(this._selectionMap.values()).forEach((sel) => sel.saveRun(ls));
    }

    saveHelp(resp: SEARCH_HELP) {
        if (!this.isActive) return;
        const ls: SEARCH_ITEM_HELP_DATA[] = [];
        resp.selectionLs = ls;

        Array.from(this._selectionMap.values()).forEach((sel) => sel.saveHelp(ls));
    }

    selectionParams(code: string) {
        return this._selectionMap.get(code);
    }
}
